export type PrintTab = "submittals" | "materials";

export type PrintPayload = {
  importLogId: string;
  sourceLogId: string;
  tab: PrintTab;
  filteredIds: string[];
  // Optional human-readable schedule names. The print view header renders
  // "Compared with: <importScheduleName>" instead of opaque UUIDs when
  // these are present.
  sourceScheduleName?: string;
  importScheduleName?: string;
  // Toggleable column ids visible in the grid at print launch. Absent
  // means "no column-visibility info" — print view falls back to showing
  // all columns (back-compat with older URLs / tests).
  visibleColumnIds?: string[];
};

const HASH_KEY = "p";
// Conservative budget across browsers: Firefox ~64KB, Safari ~80KB, Chrome
// effectively unbounded for fragments. A warning at 60KB lets us notice
// if real usage starts pushing that ceiling.
const HASH_WARN_AT = 60_000;

// base64-url encode that survives `noopener` window.open boundaries and
// works regardless of storage isolation. Storing the data in the URL
// fragment also makes the link shareable — anyone with the URL gets the
// same filtered view.
function encode(payload: PrintPayload): string {
  const json = JSON.stringify(payload);
  const b64 =
    typeof btoa === "function"
      ? btoa(unescape(encodeURIComponent(json)))
      : Buffer.from(json, "utf-8").toString("base64");

  // Safe, non-regex removal of all trailing "=" padding in the base64 string
  let trimmed = b64;
  let len = trimmed.length;
  while (len > 0 && trimmed[len - 1] === "=") {
    len--;
  }
  trimmed = trimmed.slice(0, len);

  // Replace "+" with "-", and "/" with "_"
  trimmed = trimmed.replace(/\+/g, "-").replace(/\//g, "_");

  return trimmed;
}

function decode(s: string): PrintPayload | null {
  try {
    const b64 = s.replace(/-/g, "+").replace(/_/g, "/");
    const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
    const json =
      typeof atob === "function"
        ? decodeURIComponent(escape(atob(padded)))
        : Buffer.from(padded, "base64").toString("utf-8");
    return JSON.parse(json) as PrintPayload;
  } catch {
    return null;
  }
}

export function buildPrintPayloadHash(payload: PrintPayload): string {
  const encoded = encode(payload);
  if (encoded.length > HASH_WARN_AT && typeof console !== "undefined") {
    console.warn(
      `Print payload hash is ${encoded.length} chars — Firefox/Safari ` +
        "cap URL fragments around 64–80 KB. If exports start failing on " +
        "those browsers, consider trimming the filtered set first."
    );
  }
  return `${HASH_KEY}=${encoded}`;
}

export function readPrintPayloadFromHash(
  hash: string | null | undefined
): PrintPayload | null {
  if (!hash) return null;
  const stripped = hash.startsWith("#") ? hash.slice(1) : hash;
  const params = new URLSearchParams(stripped);
  const raw = params.get(HASH_KEY);
  if (!raw) return null;
  return decode(raw);
}
